import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { curriculumRequirementActions } from "../slices/curriculumRequirement";
import { getErrorMessage } from "../../utils/errorUtils";
import { CurriculumRequirementRequestDto } from "../../shared/dtos/curriculum/CurriculumRequirementRequestDto";
import { CurriculumRequirementResponseDto } from "../../shared/dtos/curriculum/CurriculumRequirementResponseDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const createCurriculumRequirement =
  (values: CurriculumRequirementRequestDto) =>
  async (dispatch: AppDispatch) => {
    dispatch(curriculumRequirementActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<CurriculumRequirementResponseDto>
      >("/curriculum-requirements", values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(
          data.message || "Curriculum requirement created successfully!",
        );
        dispatch(curriculumRequirementActions.createSuccess(data.data));
        dispatch(
          curriculumRequirementActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(curriculumRequirementActions.setErrorCreate(message));
    }
  };

export const getCurriculumRequirements =
  (params?: { branchId?: string }) => async (dispatch: AppDispatch) => {
    dispatch(curriculumRequirementActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<CurriculumRequirementResponseDto[]>
      >("/curriculum-requirements", {
        headers: { requiresAuth: true },
        params,
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(curriculumRequirementActions.setItems(data.data));
      }
    } catch (error) {
      dispatch(
        curriculumRequirementActions.setErrorFetchAll(getErrorMessage(error)),
      );
    }
  };

export const getCurriculumRequirementById =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(curriculumRequirementActions.setLoadingFetchOne());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<CurriculumRequirementResponseDto>
      >(`/curriculum-requirements/${id}`, { headers: { requiresAuth: true } });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(curriculumRequirementActions.setItem(data.data));
      }
    } catch (error) {
      dispatch(
        curriculumRequirementActions.setErrorFetchOne(getErrorMessage(error)),
      );
    }
  };

export const updateCurriculumRequirement =
  (payload: { id: string; values: Partial<CurriculumRequirementRequestDto> }) =>
  async (dispatch: AppDispatch) => {
    dispatch(curriculumRequirementActions.setLoadingUpdate());
    try {
      const { data } = await axiosInstance.put<
        ApiResponseDto<CurriculumRequirementResponseDto>
      >(`/curriculum-requirements/${payload.id}`, payload.values, {
        headers: { requiresAuth: true },
      });

      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(
          data.message || "Curriculum requirement updated successfully!",
        );
        dispatch(curriculumRequirementActions.updateSuccess(data.data));
        dispatch(
          curriculumRequirementActions.setServerStatus({
            operation: "update",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(curriculumRequirementActions.setErrorUpdate(message));
    }
  };

export const deleteCurriculumRequirement =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(curriculumRequirementActions.setLoadingDelete());
    try {
      const { data } = await axiosInstance.delete<ApiResponseDto<void>>(
        `/curriculum-requirements/${id}`,
        { headers: { requiresAuth: true } },
      );

      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success(
          data.message || "Curriculum requirement deleted successfully!",
        );
        dispatch(curriculumRequirementActions.deleteSuccess(id));
        dispatch(
          curriculumRequirementActions.setServerStatus({
            operation: "delete",
            status: data.statusCode || 200,
          }),
        );
      }
    } catch (error) {
      const message = getErrorMessage(error);
      toast.error(message);
      dispatch(curriculumRequirementActions.setErrorDelete(message));
    }
  };

export const resetCurriculumRequirementServerStatus =
  () => (dispatch: AppDispatch) => {
    dispatch(curriculumRequirementActions.setServerStatus(null));
  };
